import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { stripUnit } from 'polished'

import fluidRange from '../../utils/fluidRange'
import SectionComponent from '../Section'

export const Section = styled(SectionComponent)(
  ({
    theme,
    imageWidth,
    alignMainContent,
    alignImageToBottom,
    mobileAlignImageToBottom,
    mobileImage,
    reverseForMobile,
    hideImageOnMobile,
  }) => {
    const oneThird = stripUnit(theme.breakpoints.xl) / 3
    const oneFourth = stripUnit(theme.breakpoints.xl) / 4
    const width = stripUnit(imageWidth) || stripUnit(theme.breakpoints.xl)

    const gridTemplateColumns = ['1fr', '1fr']
    const gridTemplateAreas = ['image', 'content']
    const reverseMobileTemplateAreas = mobileAlignImageToBottom
      ? true
      : reverseForMobile
    let mobileTemplateAreas = '"image" "content"'

    if (reverseMobileTemplateAreas) {
      mobileTemplateAreas = '"content" "image"'
    }

    /**
     * Determine if the image should be smaller than half the container
     *  and set the template areas to position the image on the correct side
     */
    if (width < oneFourth) {
      gridTemplateAreas.push('content')
      gridTemplateColumns.push('1fr')
    }
    if (width < oneThird) {
      gridTemplateAreas.push('content')
      gridTemplateColumns.push('1fr')
    }

    return css`
      @media screen and (min-width: 769px) {
        ${alignImageToBottom && `padding-bottom: 0 !important`};
      }

      @media screen and (max-width: ${theme.breakpoints.md}) {
        ${mobileAlignImageToBottom && `padding-bottom: 0 !important`};
      }

      &:nth-of-type(odd) {
        .split-content__grid {
          @media screen and (min-width: 769px) {
            grid-template-areas: '${[...gridTemplateAreas]
              .reverse()
              .join(' ')}';
          }
        }
      }

      .split-content {
        &__grid {
          display: grid;
          align-items: center;
          grid-template-rows: auto auto;
          grid-template-areas: ${mobileTemplateAreas};
          ${fluidRange('grid-gap', [
            stripUnit(theme.spacing.lg),
            stripUnit(theme.spacing.xxl),
          ])};

          @media screen and (min-width: 769px) {
            grid-template-areas: '${gridTemplateAreas.join(' ')}';
            grid-template-columns: ${gridTemplateColumns.join(' ')};
            grid-template-rows: none;
          }
        }

        &__image {
          @media screen and (min-width: 769px) {
            align-self: ${alignImageToBottom ? 'flex-end' : 'center'};
          }

          @media screen and (max-width: ${theme.breakpoints.md}) {
            align-self: ${mobileAlignImageToBottom ? 'flex-end' : 'center'};
          }

          margin: 0;
          padding: 0;
          text-align: center;
          ${hideImageOnMobile ? 'display: none' : null};
          grid-area: image;

          @media screen and (min-width: 769px) {
            display: block;
          }
        }

        &__content {
          display: flex;
          flex-direction: column;
          min-width: 0;
          align-items: ${alignMainContent === 'center'
            ? 'center'
            : 'flex-start'};
          text-align: ${alignMainContent === 'center' ? 'center' : 'left'};
          grid-area: content;

          .leshen-stack {
            max-width: 100%;
          }

          @media screen and (min-width: 769px) {
            ${alignImageToBottom &&
            fluidRange('padding-bottom', [
              stripUnit(theme.spacing.lg),
              stripUnit(theme.spacing.xxl),
            ])};
          }

          @media screen and (max-width: ${theme.breakpoints.md}) {
            ${mobileAlignImageToBottom && mobileImage && `padding-bottom: 0`};

            ${mobileAlignImageToBottom &&
            fluidRange('padding-bottom', [
              stripUnit(theme.spacing.lg),
              stripUnit(theme.spacing.xxl),
            ])};
          }
        }
      }
    `
  }
)
export const Image = styled.div(
  ({
    theme,
    mobileImage,
    aspectRatio,
    borderRadius,
    customPosition,
    mobileCustomPosition,
    mobileBorderRadius,
    mobileAspectRatio,
  }) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
      .gatsby-image-wrapper {
        aspect-ratio: ${aspectRatio};
        border-radius: ${borderRadius};
        img {
          object-position: ${customPosition};
        }
      }
      .mobile-image {
        display: none;
      }
    }

    @media (max-width: 539px) {
      .gatsby-image-wrapper {
        aspect-ratio: ${mobileAspectRatio};
        border-radius: ${mobileBorderRadius};
        img {
          object-position: ${mobileCustomPosition};
        }
      }
      .desktop-image {
        display: ${mobileImage ? 'none' : 'block'};
      }
    }
  `
)

export const BGImage = styled.div`
  display: block;
  position: absolute !important;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  // In decoupling from Gatsby image, i need to target the inline styles generated by gatsby on that div

  > .gatsby-image-wrapper {
    position: initial;

    // Gatsby Image has inline-styles with padding-bottom overflow into other sections.

    > div {
      padding-bottom: 0 !important;
    }

    picture {
      img {
        height: 100%;
      }
    }
  }

  > img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`

export const BackgroundImage = styled(BGImage)`
  ${(props) =>
    props.mobileBackgroundImage &&
    `
    @media (max-width: 539px) {
      display: none;
    }
  `}
`

export const MobileBackgroundImage = styled(BGImage)`
  @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
    display: none;
  }
`

export const SplitContentGrid = styled.div`
  ${(props) => props.hasBackgroundImage && `position: relative;`}
`
